import type { PolygonDrawer, PolygonDrawerOptions } from '../terra-draw/terra-draw-polygon';

/**
 * TEMPORARY - the pre-Terra-Draw drawer, kept only as the `SERVICE_AREAS_TERRA_DRAW_FF` off-path
 * so the migration can be rolled back without a deploy. Delete this whole folder once the flag is
 * fully rolled out; the removal checklist lives next to the flag in ../../constants.ts.
 *
 * Depends on google.maps.drawing, which Google removed in Maps JS API v3.65 (INC-3414) - the
 * loaders pin v=3.64 for exactly as long as this file exists.
 */
export function createLegacyPolygonDrawer({
	map,
	controlPosition,
	polygonOptions,
	onDrawingChange,
	onPolygonComplete
}: PolygonDrawerOptions): PolygonDrawer {
	let drawingManager: google.maps.drawing.DrawingManager = null;

	const isDrawing = () =>
		drawingManager != null && drawingManager.getDrawingMode() === google.maps.drawing.OverlayType.POLYGON;

	// Google can retire the pinned version at any time - if it does, keep the map alive without the
	// draw tool rather than taking the whole Service Areas page down with it
	try {
		drawingManager = new google.maps.drawing.DrawingManager({
			map,
			polygonOptions,
			drawingControlOptions: {
				position: controlPosition,
				drawingModes: [google.maps.drawing.OverlayType.POLYGON]
			}
		});

		google.maps.event.addListener(drawingManager, 'drawingmode_changed', () => onDrawingChange?.(isDrawing()));

		google.maps.event.addListener(drawingManager, 'polygoncomplete', polygon => {
			drawingManager.setDrawingMode(null);
			onPolygonComplete(polygon);
		});
	} catch (error) {
		console.error('DrawingManager unavailable, service area drawing disabled', error);
		drawingManager = null;
	}

	return {
		isDrawing,
		setDrawing: drawing => drawingManager?.setDrawingMode(drawing ? google.maps.drawing.OverlayType.POLYGON : null),
		setControlVisible: visible => drawingManager?.setOptions({ drawingControl: visible }),
		destroy: () => {
			if (!drawingManager) {
				return;
			}

			google.maps.event.clearInstanceListeners(drawingManager);
			drawingManager.setMap(null);
			drawingManager = null;
		}
	};
}
